import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/layout/Navigation';
import { isIOS } from '../services/platform.service';
import {
  MapPin,
  Star,
  Eye,
  Edit,
  CheckCircle,
  AlertCircle,
  Users,
  CalendarCheck,
  BarChart3,
  Heart,
  TrendingUp,
  Plus,
  Bell,
  Building2,
  Phone,
  Globe,
  FileText,
  Megaphone,
  Clock,
  ChevronRight,
  BadgeCheck,
  Sparkles,
  Lock,
} from 'lucide-react';

// Plan feature gates
const PLAN_FEATURES: Record<string, string[]> = {
  BASIC: ['profile', 'map', 'contact', 'badge'],
  PRO: ['profile', 'map', 'contact', 'badge', 'bookings', 'posts', 'stats', 'priority_map'],
  PREMIUM: ['profile', 'map', 'contact', 'badge', 'bookings', 'posts', 'stats', 'priority_map', 'ai_targeting', 'banner', 'push', 'crm', 'analytics'],
};

function hasFeature(plan: string, feature: string): boolean {
  return (PLAN_FEATURES[plan] || PLAN_FEATURES['BASIC']).includes(feature);
}

function PlanBadge({ plan }: { plan: string }) {
  const colors: Record<string, string> = {
    BASIC: 'bg-gray-100 text-gray-700 border-gray-300',
    PRO: 'bg-teal-100 text-teal-700 border-teal-300',
    PREMIUM: 'bg-indigo-100 text-indigo-700 border-indigo-300',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${colors[plan] || colors.BASIC}`}>
      <BadgeCheck className="w-3 h-3" />
      {plan}
    </span>
  );
}

function LockedFeature({ label, upgradeText }: { label: string; upgradeText: string }) {
  const navigate = useNavigate();
  if (isIOS()) return null; // plan upgrades are not offered in the iOS app
  return (
    <div className="relative p-5 bg-gray-50 border border-dashed border-gray-300 rounded-xl flex items-center gap-3 opacity-70">
      <Lock className="w-5 h-5 text-gray-400 flex-shrink-0" />
      <div className="flex-1">
        <p className="text-sm font-semibold text-gray-500">{label}</p>
        {!isIOS() && <p className="text-xs text-gray-400">{upgradeText}</p>}
      </div>
      {!isIOS() && (
      <button onClick={() => navigate('/business')} className="text-xs font-bold text-teal-600 hover:text-teal-700 whitespace-nowrap">Upgrade →</button>
      )}
    </div>
  );
}

export default function SpecialistDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hasProfile, setHasProfile] = useState(false);
  const [profileData, setProfileData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'team' | 'bookings' | 'posts' | 'reviews' | 'stats'>('overview');

  // Detect plan from profile description or default to BASIC
  const detectedPlan = (() => {
    if (!profileData?.description) return 'BASIC';
    const match = profileData.description.match(/Plan:\s*(BASIC|PRO|PREMIUM)/i);
    return match ? match[1].toUpperCase() : 'BASIC';
  })();

  // Redirect non-specialists
  useEffect(() => {
    if (user && user.userType !== 'SPECIALIST') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Fetch specialist profile
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) return;
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/specialists?status=APPROVED`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          const myProfile = data.specialists?.find((s: any) => s.submittedById === user.id);
          if (myProfile) {
            setHasProfile(true);
            setProfileData(myProfile);
          }
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [user]);

  const tabs: { id: 'overview' | 'team' | 'bookings' | 'posts' | 'reviews' | 'stats'; label: string; icon: any; locked?: boolean }[] = [
    { id: 'overview', label: 'Prezentare', icon: Building2, locked: false },
    { id: 'team', label: 'Echipă', icon: Users, locked: false },
    { id: 'bookings', label: 'Programări', icon: CalendarCheck, locked: !hasFeature(detectedPlan, 'bookings') },
    { id: 'posts', label: 'Postări', icon: Megaphone, locked: !hasFeature(detectedPlan, 'posts') },
    { id: 'reviews', label: 'Recenzii', icon: Star, locked: false },
    { id: 'stats', label: 'Statistici', icon: BarChart3, locked: !hasFeature(detectedPlan, 'stats') },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* ── Hero Banner ─────────────────────────────────────────────── */}
        <div className="mb-6 bg-gradient-to-r from-teal-600 to-teal-800 rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 opacity-10">
            <div className="w-64 h-64 bg-white rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          </div>
          <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl sm:text-3xl font-extrabold">
                  FII FIT Business
                </h1>
                <PlanBadge plan={detectedPlan} />
              </div>
              <p className="text-teal-100 text-sm sm:text-base">
                {hasProfile
                  ? `${profileData?.name || user?.firstName} — ${profileData?.city || ''}`
                  : `Bun venit, ${user?.firstName || 'Specialist'}! Completează profilul pentru a fi vizibil.`}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/specialist/profile/edit')}
                className="flex items-center gap-2 bg-white text-teal-700 px-4 py-2.5 rounded-xl font-bold hover:bg-teal-50 transition text-sm shadow"
              >
                <Edit className="w-4 h-4" />
                {hasProfile ? 'Editează Profilul' : 'Completează Profilul'}
              </button>
              <button
                onClick={() => navigate('/specialists')}
                className="flex items-center gap-2 bg-teal-500 text-white px-4 py-2.5 rounded-xl font-bold hover:bg-teal-400 transition text-sm"
              >
                <Eye className="w-4 h-4" />
                Vezi Profil Public
              </button>
            </div>
          </div>
        </div>

        {/* ── Profile incomplete warning ───────────────────────────────── */}
        {!loading && !hasProfile && (
          <div className="mb-6 p-5 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-bold text-amber-800">Profilul tău nu este complet</p>
              <p className="text-sm text-amber-700 mt-1">
                Completează profilul pentru a apărea în Harta Specialiștilor și a fi descoperit de clienți.
              </p>
            </div>
            <button
              onClick={() => navigate('/specialist/profile/edit')}
              className="bg-amber-500 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-amber-600 transition flex-shrink-0"
            >
              Completează acum
            </button>
          </div>
        )}

        {/* ── Stats Row ───────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            {
              label: 'Vizualizări',
              value: profileData?.viewCount ?? 0,
              icon: Eye,
              color: 'text-blue-600',
              bg: 'bg-blue-50',
              feature: 'profile',
            },
            {
              label: 'Favorite',
              value: profileData?.favoriteCount ?? 0,
              icon: Heart,
              color: 'text-rose-600',
              bg: 'bg-rose-50',
              feature: 'profile',
            },
            {
              label: 'Programări',
              value: profileData?.bookingCount ?? 0,
              icon: CalendarCheck,
              color: 'text-teal-600',
              bg: 'bg-teal-50',
              feature: 'bookings',
            },
            {
              label: 'Rating',
              value: profileData?.averageRating ? profileData.averageRating.toFixed(1) : '-',
              icon: Star,
              color: 'text-yellow-600',
              bg: 'bg-yellow-50',
              feature: 'profile',
            },
          ].filter((stat) => !isIOS() || hasFeature(detectedPlan, stat.feature)).map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{stat.label}</p>
                <div className={`w-9 h-9 ${stat.bg} rounded-lg flex items-center justify-center`}>
                  <stat.icon className={`w-4 h-4 ${stat.color}`} />
                </div>
              </div>
              {hasFeature(detectedPlan, stat.feature) ? (
                <p className="text-2xl font-extrabold text-gray-900">{stat.value}</p>
              ) : (
                <div className="flex items-center gap-1">
                  <Lock className="w-4 h-4 text-gray-300" />
                  <span className="text-sm text-gray-400">Upgrade</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ── Tabs ────────────────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {/* Tab bar */}
          <div className="flex overflow-x-auto border-b border-gray-100 scrollbar-hide">
            {tabs.filter((tab) => !isIOS() || !tab.locked).map((tab) => (
              <button
                key={tab.id}
                onClick={() => !tab.locked && setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold whitespace-nowrap border-b-2 transition-all flex-shrink-0 ${
                  activeTab === tab.id
                    ? 'border-teal-600 text-teal-700 bg-teal-50/50'
                    : tab.locked
                    ? 'border-transparent text-gray-300 cursor-not-allowed'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.locked && <Lock className="w-3 h-3 ml-1" />}
              </button>
            ))}
          </div>

          <div className="p-6">

            {/* ── TAB: Overview ─────────────────────────────────────── */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {hasProfile ? (
                  <>
                    {/* Profile summary */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <h3 className="font-bold text-gray-900 text-lg">Informații Centru</h3>
                        <div className="space-y-3">
                          {[
                            { icon: Building2, label: 'Nume', value: profileData?.name },
                            { icon: MapPin, label: 'Locație', value: profileData ? `${profileData.city}, ${profileData.county}` : '-' },
                            { icon: Phone, label: 'Telefon', value: profileData?.phone },
                            { icon: Globe, label: 'Website', value: profileData?.website },
                          ].map((item) => item.value && (
                            <div key={item.label} className="flex items-start gap-3">
                              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <item.icon className="w-4 h-4 text-gray-500" />
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">{item.label}</p>
                                <p className="text-sm font-medium text-gray-900">{item.value}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-4">
                        <h3 className="font-bold text-gray-900 text-lg">Descriere</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {profileData?.description || 'Nicio descriere adăugată. Editează profilul pentru a adăuga o descriere.'}
                        </p>
                        {profileData?.specializations?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-2">Specializări</p>
                            <div className="flex flex-wrap gap-2">
                              {profileData.specializations.map((s: string) => (
                                <span key={s} className="px-2.5 py-1 bg-teal-50 text-teal-700 text-xs font-semibold rounded-full border border-teal-200">
                                  {s}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Plan features overview */}
                    <div className="border-t pt-6">
                      <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-teal-600" />
                        {isIOS() ? 'Funcționalitățile contului tău' : <>Funcționalitățile planului tău — <PlanBadge plan={detectedPlan} /></>}
                      </h3>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {[
                          { feature: 'profile', label: 'Pagină profil business', icon: Building2 },
                          { feature: 'map', label: 'Apariție în Harta Specialiști', icon: MapPin },
                          { feature: 'contact', label: 'Buton contact direct', icon: Phone },
                          { feature: 'badge', label: 'Badge Verificat FII FIT', icon: BadgeCheck },
                          { feature: 'bookings', label: 'Programări online', icon: CalendarCheck },
                          { feature: 'posts', label: 'Postări & Noutăți', icon: Megaphone },
                          { feature: 'stats', label: 'Statistici în timp real', icon: BarChart3 },
                          { feature: 'priority_map', label: 'Poziționare prioritară hartă', icon: TrendingUp },
                          { feature: 'ai_targeting', label: 'Recomandări AI targetate', icon: Sparkles },
                          { feature: 'banner', label: 'Banner promoțional în app', icon: Bell },
                        ].filter((item) => !isIOS() || hasFeature(detectedPlan, item.feature)).map((item) => {
                          const active = hasFeature(detectedPlan, item.feature);
                          return (
                            <div
                              key={item.feature}
                              className={`flex items-center gap-3 p-3 rounded-xl border ${
                                active ? 'bg-teal-50 border-teal-200' : 'bg-gray-50 border-gray-200 opacity-60'
                              }`}
                            >
                              {active ? (
                                <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
                              ) : (
                                <Lock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              )}
                              <span className={`text-xs font-semibold ${active ? 'text-teal-800' : 'text-gray-400'}`}>
                                {item.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      {detectedPlan !== 'PREMIUM' && !isIOS() && (
                        <button
                          onClick={() => navigate('/business')}
                          className="mt-4 flex items-center gap-2 text-sm font-bold text-teal-600 hover:text-teal-700"
                        >
                          <TrendingUp className="w-4 h-4" />
                          Upgrade pentru mai multe funcționalități →
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-16">
                    <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-xl font-bold text-gray-700 mb-2">Profilul tău nu este configurat</p>
                    <p className="text-gray-500 mb-6 max-w-md mx-auto">
                      Completează profilul centrului tău pentru a apărea în harta specialiștilor și a fi descoperit de clienți.
                    </p>
                    <button
                      onClick={() => navigate('/specialist/profile/edit')}
                      className="bg-teal-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-teal-700 transition shadow-lg"
                    >
                      Completează Profilul Acum
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ── TAB: Echipă ───────────────────────────────────────── */}
            {activeTab === 'team' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg">Lista Antrenorilor / Specialiștilor</h3>

                    <p className="text-sm text-gray-500 mt-1">
                      {detectedPlan === 'BASIC'
                        ? 'Planul Basic permite până la 3 specialiști listați.'
                        : 'Specialiști nelimitați pe planul tău.'}
                    </p>
                  </div>
                  <button className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2.5 rounded-xl font-bold hover:bg-teal-700 transition text-sm">
                    <Plus className="w-4 h-4" />
                    Adaugă Specialist
                  </button>
                </div>

                {/* Empty state */}
                <div className="border-2 border-dashed border-gray-200 rounded-2xl p-12 text-center">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="font-semibold text-gray-600 mb-2">Niciun specialist adăugat încă</p>
                  <p className="text-sm text-gray-400 mb-6 max-w-sm mx-auto">
                    Adaugă antrenorii și specialiștii din centrul tău cu bio, specializare, foto și program de lucru.
                  </p>
                  <button className="bg-teal-600 text-white px-6 py-2.5 rounded-xl font-bold hover:bg-teal-700 transition text-sm">
                    + Adaugă primul specialist
                  </button>
                </div>

                {/* Info about what each specialist card shows */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-sm font-semibold text-blue-800 mb-2">Ce include fiecare fișă de specialist:</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {['Foto profil', 'Nume și titlu', 'Specializare', 'Bio / descriere', 'Program de lucru', 'Buton programare'].map((item) => (
                      <div key={item} className="flex items-center gap-2 text-sm text-blue-700">
                        <CheckCircle className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── TAB: Programări ───────────────────────────────────── */}
            {activeTab === 'bookings' && (
              hasFeature(detectedPlan, 'bookings') ? (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg">Programări Online</h3>
                      <p className="text-sm text-gray-500 mt-1">Clienții se pot programa direct din aplicație.</p>

                    </div>
                    <div className="flex items-center gap-2">
                      <button className="flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2.5 rounded-xl font-semibold hover:bg-gray-50 transition text-sm">
                        Export CSV
                      </button>
                    </div>
                  </div>

                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { label: 'Azi', value: '0', color: 'text-teal-600' },
                      { label: 'Această săptămână', value: '0', color: 'text-blue-600' },
                      { label: 'Total', value: profileData?.bookingCount ?? '0', color: 'text-purple-600' },
                    ].map((s) => (
                      <div key={s.label} className="bg-gray-50 rounded-xl p-4 text-center border border-gray-100">
                        <p className={`text-2xl font-extrabold ${s.color}`}>{s.value}</p>
                        <p className="text-xs text-gray-500 mt-1">{s.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Empty state */}
                  <div className="border-2 border-dashed border-gray-200 rounded-2xl p-12 text-center">
                    <CalendarCheck className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="font-semibold text-gray-600 mb-2">Nicio programare încă</p>
                    <p className="text-sm text-gray-400 max-w-sm mx-auto">
                      Programările vor apărea aici când clienții folosesc butonul „Programează-te" din profilul tău.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="py-8">
                  <LockedFeature
                    label="Programări Online"
                    upgradeText="Disponibil în planul Pro și Premium. Clienții se pot programa direct din aplicație."

                  />
                </div>
              )
            )}

            {/* ── TAB: Postări ──────────────────────────────────────── */}
            {activeTab === 'posts' && (
              hasFeature(detectedPlan, 'posts') ? (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg">Postări & Noutăți</h3>
                      <p className="text-sm text-gray-500 mt-1">Publică articole, promoții și evenimente.</p>
                    </div>
                    <button className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2.5 rounded-xl font-bold hover:bg-teal-700 transition text-sm">
                      <Plus className="w-4 h-4" />
                      Postare nouă
                    </button>
                  </div>

                  {/* Post type buttons */}
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: 'Articol', icon: FileText, color: 'bg-blue-50 border-blue-200 text-blue-700' },
                      { label: 'Promoție', icon: Megaphone, color: 'bg-orange-50 border-orange-200 text-orange-700' },
                      { label: 'Eveniment', icon: Clock, color: 'bg-purple-50 border-purple-200 text-purple-700' },
                    ].map((type) => (
                      <button
                        key={type.label}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 ${type.color} hover:opacity-80 transition`}
                      >
                        <type.icon className="w-6 h-6" />
                        <span className="text-sm font-bold">{type.label}</span>
                      </button>
                    ))}
                  </div>

                  {/* Empty state */}
                  <div className="border-2 border-dashed border-gray-200 rounded-2xl p-12 text-center">
                    <Megaphone className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="font-semibold text-gray-600 mb-2">Nicio postare publicată</p>
                    <p className="text-sm text-gray-400 max-w-sm mx-auto">
                      Publică articole, promoții sau evenimente pentru a atrage mai mulți clienți.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="py-8">
                  <LockedFeature
                    label="Postări & Noutăți"
                    upgradeText="Disponibil în planul Pro și Premium. Publică articole, promoții și evenimente."
                  />
                </div>
              )
            )}

            {/* ── TAB: Recenzii ─────────────────────────────────────── */}
            {activeTab === 'reviews' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg">Recenzii Verificate</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Recenzii autentice de la utilizatorii FII FIT care au consultat centrul tău.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-200 px-3 py-2 rounded-xl">
                    <Star className="w-4 h-4 text-yellow-500 fill-current" />
                    <span className="font-bold text-yellow-800">
                      {profileData?.averageRating ? profileData.averageRating.toFixed(1) : '-'}
                    </span>
                    <span className="text-xs text-yellow-600">
                      ({profileData?.totalReviews || 0} recenzii)
                    </span>
                  </div>
                </div>

                {!profileData?.reviews || profileData.reviews.length === 0 ? (
                  <div className="border-2 border-dashed border-gray-200 rounded-2xl p-12 text-center">
                    <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="font-semibold text-gray-600 mb-2">Nicio recenzie încă</p>
                    <p className="text-sm text-gray-400 max-w-sm mx-auto">
                      Recenziile vor apărea automat după ce clienții FII FIT consultă centrul tău și lasă o evaluare.

                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {profileData.reviews.map((review: any) => (
                      <div key={review.id} className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-teal-100 rounded-full flex items-center justify-center text-teal-700 font-bold text-sm">
                              {review.isAnonymous ? '?' : (review.user?.firstName?.[0] || 'U')}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900 text-sm">
                                {review.isAnonymous ? 'Utilizator Anonim' : `${review.user?.firstName || ''} ${review.user?.lastName || ''}`}
                              </p>
                              <div className="flex items-center gap-1 mt-0.5">
                                {[...Array(5)].map((_, i) => (
                                  <Star key={i} className={`w-3.5 h-3.5 ${i < review.rating ? 'text-yellow-500 fill-current' : 'text-gray-300'}`} />
                                ))}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {review.wouldRecommend && (
                              <span className="text-xs text-green-600 font-semibold bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
                                ✓ Recomandă
                              </span>
                            )}
                            <span className="text-xs text-gray-400">
                              {new Date(review.createdAt).toLocaleDateString('ro-RO')}
                            </span>
                          </div>
                        </div>
                        {review.comment && (
                          <p className="text-gray-700 text-sm leading-relaxed">{review.comment}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── TAB: Statistici ───────────────────────────────────── */}
            {activeTab === 'stats' && (
              hasFeature(detectedPlan, 'stats') ? (
                <div className="space-y-6">
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg">Statistici Pagină</h3>
                    <p className="text-sm text-gray-500 mt-1">Date în timp real despre vizibilitatea centrului tău.</p>
                  </div>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { label: 'Vizualizări totale', value: profileData?.viewCount ?? 0, icon: Eye, color: 'text-blue-600', bg: 'bg-blue-50' },
                      { label: 'Adăugat la favorite', value: profileData?.favoriteCount ?? 0, icon: Heart, color: 'text-rose-600', bg: 'bg-rose-50' },
                      { label: 'Programări primite', value: profileData?.bookingCount ?? 0, icon: CalendarCheck, color: 'text-teal-600', bg: 'bg-teal-50' },
                      { label: 'Recenzii primite', value: profileData?.totalReviews ?? 0, icon: Star, color: 'text-yellow-600', bg: 'bg-yellow-50' },
                    ].map((stat) => (
                      <div key={stat.label} className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
                        <div className={`w-10 h-10 ${stat.bg} rounded-xl flex items-center justify-center mb-3`}>
                          <stat.icon className={`w-5 h-5 ${stat.color}`} />
                        </div>
                        <p className="text-2xl font-extrabold text-gray-900">{stat.value}</p>
                        <p className="text-xs text-gray-500 mt-1">{stat.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* AI Targeting - Premium only */}
                  {hasFeature(detectedPlan, 'ai_targeting') ? (
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <Sparkles className="w-5 h-5 text-indigo-600" />
                        <h4 className="font-bold text-indigo-800">Recomandări AI Targetate</h4>
                      </div>
                      <p className="text-sm text-indigo-700">
                        Centrul tău este recomandat automat utilizatorilor cu afecțiunile relevante pentru specializările tale.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(profileData?.specializations || []).map((s: string) => (
                          <span key={s} className="px-2.5 py-1 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-full">
                            {s}
                          </span>
                        ))}
                        {(!profileData?.specializations || profileData.specializations.length === 0) && (
                          <span className="text-xs text-indigo-500">Adaugă specializări în profil pentru targetare AI.</span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <LockedFeature
                      label="Recomandări AI Targetate"
                      upgradeText="Disponibil în planul Premium. Centrul tău apare recomandat utilizatorilor cu afecțiunile relevante."
                    />
                  )}

                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
                    <p className="text-sm text-gray-500">
                      📊 Graficele detaliate de evoluție se completează pe măsură ce primești clienți.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="py-8">
                  <LockedFeature
                    label="Statistici în Timp Real"
                    upgradeText="Disponibil în planul Pro și Premium. Vizualizări, favorite, programări — toate datele relevante."
                  />
                </div>
              )
            )}

          </div>
        </div>

        {/* ── Quick Actions ───────────────────────────────────────────── */}
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              label: 'Editează Profilul',
              desc: 'Actualizează informațiile centrului',
              icon: Edit,
              color: 'bg-blue-50 border-blue-200 text-blue-700',
              action: () => navigate('/specialist/profile/edit'),
            },
            {
              label: 'Harta Specialiști',
              desc: 'Vezi cum apari pe hartă',
              icon: MapPin,
              color: 'bg-teal-50 border-teal-200 text-teal-700',
              action: () => navigate('/specialists'),
            },
            ...(isIOS() ? [] : [{
              label: 'Upgrade Plan',
              desc: 'Deblochează mai multe funcții',
              icon: TrendingUp,
              color: 'bg-indigo-50 border-indigo-200 text-indigo-700',
              action: () => navigate('/business'),
            }]),
            {
              label: 'Suport FII FIT',
              desc: 'Contactează echipa noastră',
              icon: Bell,
              color: 'bg-orange-50 border-orange-200 text-orange-700',
              action: () => navigate('/contact'),
            },
          ].map((action) => (
            <button
              key={action.label}
              onClick={action.action}
              className={`flex items-center gap-4 p-4 rounded-xl border-2 ${action.color} hover:opacity-80 transition text-left`}
            >
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                <action.icon className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="font-bold text-sm">{action.label}</p>
                <p className="text-xs opacity-70 truncate">{action.desc}</p>
              </div>
              <ChevronRight className="w-4 h-4 ml-auto flex-shrink-0 opacity-50" />
            </button>
          ))}
        </div>

      </main>
    </div>
  );
}
